import React, { useRef, useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Paper,
  Divider,
  Button,
  Group,
  ActionIcon,
  Tabs,
  Menu,
  Select,
  TextInput
} from '@mantine/core';
import {
  IconBold,
  IconItalic,
  IconUnderline,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconList,
  IconListNumbers,
  IconTable,
  IconChartBar,
  IconFileDownload,
  IconDeviceFloppy,
  IconClearFormatting
} from '@tabler/icons-react';

/* eslint-disable */
interface RichTextEditorProps {
  initialContent?: string;
  onSave?: (content: string) => void;
  onExport?: (content: string) => void;
  contentRef?: React.RefObject<HTMLDivElement>;
  placeholder?: string;
  height?: number | string;
}
/* eslint-enable */

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialContent = '',
  onSave,
  onExport,
  contentRef: externalContentRef,
  placeholder = 'Start typing or paste your content here...',
  height = 300
}) => {
  const internalContentRef = useRef<HTMLDivElement>(null);
  const contentDivRef = externalContentRef || internalContentRef;
  const [activeTab, setActiveTab] = useState<string | null>('edit');
  const [showTableMenu, setShowTableMenu] = useState(false);

  useEffect(() => {
    if (contentDivRef.current && initialContent) {
      contentDivRef.current.innerHTML = DOMPurify.sanitize(initialContent);
    }
  }, [initialContent, contentDivRef]);

  // Command execution helper function
  const execCommand = (command: string, value: string = '') => {
    document.execCommand(command, false, value);
    contentDivRef.current?.focus();
  };

  const toggleBold = () => execCommand('bold');
  const toggleItalic = () => execCommand('italic');
  const toggleUnderline = () => execCommand('underline');
  const alignText = (alignment: string) => execCommand('justify' + alignment);
  const clearFormatting = () => execCommand('removeFormat');

  const createBulletList = () => execCommand('insertUnorderedList');
  const createNumberedList = () => execCommand('insertOrderedList');

  const insertTable = (rows: number, cols: number) => {
    let tableHTML =
      '<table style="width:100%; border-collapse: collapse; border: 2px solid #000;">';

    tableHTML += '<thead><tr>';
    for (let i = 0; i < cols; i++) {
      tableHTML += `<th style="border: 2px solid #000; padding: 8px; text-align: left; background-color: #f5f5f5;">Header ${i + 1}</th>`;
    }
    tableHTML += '</tr></thead><tbody>';

    for (let i = 0; i < rows - 1; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < cols; j++) {
        tableHTML += `<td style="border: 2px solid #000; padding: 8px;">Cell ${i + 1}-${j + 1}</td>`;
      }
      tableHTML += '</tr>';
    }

    tableHTML += '</tbody></table><br>';

    execCommand('insertHTML', tableHTML);
    setShowTableMenu(false);
  };

  const insertChart = (chartType: string) => {
    const chartPlaceholder = `
      <div style="border: 2px dashed #ccc; border-radius: 4px; padding: 20px; text-align: center; margin: 10px 0;">
        <div style="color: #666; font-weight: bold;">${chartType.toUpperCase()} CHART PLACEHOLDER</div>
        <div style="color: #999; font-size: 12px; margin-top: 5px;">
          (This would be an actual chart in a production environment)
        </div>
      </div>
    `;

    execCommand('insertHTML', chartPlaceholder);
  };

  const getHtml = () => contentDivRef.current?.innerHTML || '';

  const handleSave = () => {
    if (onSave) {
      onSave(getHtml());
    }
  };

  const handleExport = () => {
    if (onExport) {
      onExport(getHtml());
    }
  };

  return (
    <Paper withBorder p={0} radius='md'>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value='edit'>Edit</Tabs.Tab>
          <Tabs.Tab value='html'>HTML</Tabs.Tab>
          <Tabs.Tab value='preview'>Preview</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='edit' pt='xs'>
          <Box p='sm'>
            <Group mb='xs'>
              <ActionIcon variant='subtle' onClick={toggleBold} title='Bold'>
                <IconBold size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={toggleItalic} title='Italic'>
                <IconItalic size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={toggleUnderline} title='Underline'>
                <IconUnderline size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={clearFormatting} title='Clear formatting'>
                <IconClearFormatting size={18} />
              </ActionIcon>

              <Divider orientation='vertical' />

              <ActionIcon variant='subtle' onClick={() => alignText('Left')} title='Align left'>
                <IconAlignLeft size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={() => alignText('Center')} title='Align center'>
                <IconAlignCenter size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={() => alignText('Right')} title='Align right'>
                <IconAlignRight size={18} />
              </ActionIcon>

              <Divider orientation='vertical' />

              <ActionIcon variant='subtle' onClick={createBulletList} title='Bullet list'>
                <IconList size={18} />
              </ActionIcon>
              <ActionIcon variant='subtle' onClick={createNumberedList} title='Numbered list'>
                <IconListNumbers size={18} />
              </ActionIcon>

              <Divider orientation='vertical' />

              <Menu opened={showTableMenu} onChange={setShowTableMenu}>
                <Menu.Target>
                  <ActionIcon variant='subtle' title='Insert table'>
                    <IconTable size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Table dimensions</Menu.Label>
                  <Box p='sm'>
                    <Group mb='xs'>
                      <Select
                        label='Rows'
                        placeholder='Rows'
                        data={[2, 3, 4, 5, 6, 7, 8].map((n) => ({
                          value: n.toString(),
                          label: n.toString()
                        }))}
                        defaultValue='3'
                      />
                      <Select
                        label='Columns'
                        placeholder='Columns'
                        data={[2, 3, 4, 5, 6].map((n) => ({
                          value: n.toString(),
                          label: n.toString()
                        }))}
                        defaultValue='3'
                      />
                    </Group>
                    <Button fullWidth onClick={() => insertTable(3, 3)}>
                      Insert table
                    </Button>
                  </Box>
                </Menu.Dropdown>
              </Menu>

              <Menu>
                <Menu.Target>
                  <ActionIcon variant='subtle' title='Insert chart'>
                    <IconChartBar size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Chart type</Menu.Label>
                  <Menu.Item onClick={() => insertChart('Bar')}>Bar Chart</Menu.Item>
                  <Menu.Item onClick={() => insertChart('Line')}>Line Chart</Menu.Item>
                  <Menu.Item onClick={() => insertChart('Pie')}>Pie Chart</Menu.Item>
                  <Menu.Item onClick={() => insertChart('Radar')}>Radar Chart</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            <Divider mb='sm' />

            <Box
              ref={contentDivRef}
              contentEditable
              style={{
                height,
                overflowY: 'auto',
                padding: '10px',
                border: '1px solid #ced4da',
                borderRadius: '4px',
                outline: 'none',
                boxShadow: '0 0 0 1px #7B2183',
                position: 'relative'
              }}
              data-placeholder={placeholder}
            />
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value='html' pt='xs'>
          <Box p='sm'>
            <TextInput
              label='HTML Title'
              placeholder='Enter document title'
              defaultValue='Assessment Report'
              mb='md'
            />
            <Textarea
              value={getHtml()}
              onChange={(e) => {
                if (contentDivRef.current) {
                  contentDivRef.current.innerHTML = DOMPurify.sanitize(e.target.value);
                }
              }}
              minRows={10}
              autosize
              maxRows={20}
              styles={{ root: { width: '100%' } }}
            />
          </Box>
        </Tabs.Panel>

        <Tabs.Panel value='preview' pt='xs'>
          <Box p='sm'>
            <Paper withBorder p='md' style={{ minHeight: height }}>
              <div dangerouslySetInnerHTML={{ __html: getHtml() }} />
            </Paper>
          </Box>
        </Tabs.Panel>
      </Tabs>

      <Divider />

      <Group p='sm' justify='flex-end'>
        {onSave && (
          <Button leftSection={<IconDeviceFloppy size={16} />} onClick={handleSave} color='#7B2183'>
            Save
          </Button>
        )}

        {onExport && (
          <Button
            leftSection={<IconFileDownload size={16} />}
            onClick={handleExport}
            variant='outline'
            color='#2183A1'
          >
            Export to Word
          </Button>
        )}
      </Group>
    </Paper>
  );
};

/* eslint-disable */
const Textarea: React.FC<{
  value: string;
  onChange: (e: { target: { value: string } }) => void;
  minRows?: number;
  maxRows?: number;
  autosize?: boolean;
  styles?: any;
}> = ({ value, onChange, minRows = 5, maxRows = 20, autosize = true, styles }) => {
  return (
    <textarea
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        minHeight: `${minRows * 1.5}em`,
        maxHeight: autosize ? `${maxRows * 1.5}em` : 'none',
        padding: '10px',
        border: '1px solid #ced4da',
        borderRadius: '4px',
        fontFamily: 'monospace',
        fontSize: '14px',
        lineHeight: '1.5',
        resize: 'vertical',
        ...(styles?.root || {})
      }}
    />
  );
};
/* eslint-enable */

export default RichTextEditor;
